import React, { Fragment, useImperativeHandle, useRef } from "react";
import { mergeRefs, SearchMenu, Search } from "@theorgchartdev/storybook";
import { GetRecentChartResults } from "@/Utils/DataLib";
import { useChartSearch } from "@/hooks/useChartSearch";
import { appstr } from "@coreapp/utils/appstr";
import cx from "classnames";
// 'searchMenuProps' spread only to SearchMenu and 'searchProps' only to Search; the rest configure useChartSearch.
const BasicChartSearchMenu = ({
  id,
  inputSize = "medium",
  optionSize = "medium",
  listWidth = "large",
  inputProps,
  inputOnly,
  placeholder,
  inputRef,
  onResetInput,
  launchFull = false,
  searchMenuProps,
  searchProps,
  ctrlRef,
  displayRecents = true,
  dataTestId,
  fullWidth = false,
  disableRefetchOnFocus = false,
  ...searchParams
}) => {
  const {
    doc,
    options: searchOptions,
    loading,
    queryString,
    setQueryString,
    queryResults,
    searchDisabled,
    searchHandleRef,
    onInput,
    handleInput,
    handleResetInput,
    ClearSearchQuery
  } = useChartSearch({ ...searchParams, inputOnly, onResetInput, displayRecents });

  const openCtrlRef = useRef(null);

  const customClass = fullWidth ? "ocn-basic-search-wrapper-full-width" : "";

  useImperativeHandle(ctrlRef, () => ({
    ClearSearchQuery,
    DoSearch: onInput,
    loading,
    queryResults,
    getQueryString: () => queryString,
    resetQuery: () => {
      onResetInput?.();
      searchHandleRef.current?.setValue("", true);
      setQueryString("");
    }
  }));

  const showRecents = displayRecents && GetRecentChartResults()?.length > 0;

  return (
    <Fragment>
      <span className={cx("ocn-basic-search-wrapper", customClass)} id={id}>
        {
          <React.Fragment>
            {inputOnly ? (
              <Search
                inputProps={{
                  autoFocus: true,
                  ...inputProps,
                  disabled: searchDisabled
                }}
                loading={loading}
                ref={inputRef}
                onInput={handleInput}
                placeholder={placeholder ? placeholder : appstr("Search")}
                onFocus={() => {
                  if (disableRefetchOnFocus || !doc || !launchFull || queryString.length !== 0) {
                    return;
                  }
                  onInput({ queryOrCriteria: "" });
                }}
                handleRef={searchHandleRef}
                onResetInput={handleResetInput}
                {...searchProps}
                dataTestId={dataTestId}
              />
            ) : (
              <SearchMenu
                inputProps={{ disabled: searchDisabled, ...inputProps }}
                placeholder={placeholder ? placeholder : appstr("Search")}
                searchSize={inputSize}
                options={searchOptions}
                optionSize={optionSize}
                searchCallback={str => onInput({ queryOrCriteria: str })}
                listWidth={listWidth}
                loading={loading}
                defaultInputVal={queryString}
                openCtrlRef={openCtrlRef}
                forceOpen={showRecents}
                data-testid={dataTestId}
                {...searchMenuProps}
                searchProps={{
                  ...searchMenuProps?.searchProps,
                  handleRef: mergeRefs(searchMenuProps?.searchProps?.handleRef, searchHandleRef),
                  onFocusInput: () => {
                    if (queryString?.length === 0) {
                      openCtrlRef.current?.setOpen(showRecents);
                      return;
                    }
                    if (!disableRefetchOnFocus && queryString.length !== 0) {
                      onInput({ queryOrCriteria: queryString });
                    }
                  },
                  dataTestId: dataTestId
                }}
              />
            )}
          </React.Fragment>
        }
      </span>
    </Fragment>
  );
};

export default React.memo(BasicChartSearchMenu);
